import createCache from "@emotion/cache";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useMutation } from "@tanstack/react-query";
import "react-loading-skeleton/dist/skeleton.css";
import { Route, Routes, useNavigate } from "react-router";
import rtlPlugin from "stylis-plugin-rtl";
import { lazy, Suspense } from "react";

import { CacheProvider } from "@emotion/react";
import dayjs from "dayjs";
import local from "dayjs/plugin/localeData";
import { prefixer } from "stylis";
import { CONSTANTS } from "./constants";
import AddPatientPage from "./pages/addPatient";
import ArchivePage from "./pages/archive";
import DoctorsPage from "./pages/doctors";
import GuidePage from "./pages/guide";
import Home from "./pages/home";
import NotFoundPage from "./pages/notFound";
import PatientDetailsPage from "./pages/patientDetails";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import "dayjs/locale/he";

dayjs.extend(local);
dayjs.locale("he");

import { useEffect } from "react";
import { pdfjs } from "react-pdf";
import { useLocation } from "react-router";
import FullScreenLoader from "./components/fullScreenLoader";
import { ProtectedRoute } from "./components/protectedRoute";
import { fetcher, headers } from "./network";
import { API } from "./network/api";
import TwoFactorAuthPage from "./pages/auth2fa";
import LoginPage from "./pages/login";
import PillsManagerPage from "./pages/pillsManager";

import AdminPannel from "./pages/adminPannel";
import EnterExitPatientsPage from "./pages/enterExitPatients";
import MessagePage from "./pages/message/message";
import { useHomeState } from "./states/homeState";
import { registerNotification } from "./utils/push";
import UnauthorizedPage from "./pages/unauthorizedPage";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const AddMedicationPage = lazy(() => import("./pages/addMedication"));

const rtlTheme = createTheme({
  direction: "rtl",
});

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const setHomeId = useHomeState((state) => state.setHomeId);

  const { mutate: mutateRefresh, isPending: isPendingRefresh } = useMutation({
    mutationFn: async () => {
      const { data } = await fetcher().post<{
        accessToken: string;
        homeId: string;
        is2FAComplete: boolean;
        is2FAEnabled: boolean;
      }>(API.POST.REFRESH);
      return data;
    },
    onSuccess: ({ homeId, accessToken, is2FAComplete, is2FAEnabled }) => {
      headers.authorization = `Bearer ${accessToken}`;
      setHomeId(homeId);
      if (!is2FAComplete) {
        navigate(CONSTANTS.ROUTES.auth2fa, {
          state: { homeId, is2FAEnabled: is2FAEnabled },
          replace: true,
        });
        return;
      }
      if (location.pathname === CONSTANTS.ROUTES.login) {
        registerNotification(homeId);
        navigate(CONSTANTS.ROUTES.home, { state: { homeId }, replace: true });
      }
    },
    onError: () => {
      if (location.pathname !== CONSTANTS.ROUTES.login) {
        navigate(CONSTANTS.ROUTES.login, { replace: true });
      }
    },
  });

  useEffect(() => {
    if (location.pathname === `/${CONSTANTS.ROUTES.showMessage}`) {
      return;
    }
    mutateRefresh();
  }, [mutateRefresh]);

  if (isPendingRefresh) {
    return <FullScreenLoader />;
  }

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={rtlTheme}>
        <Routes>
          <Route path={CONSTANTS.ROUTES.login} element={<LoginPage />} />
          <Route
            path={CONSTANTS.ROUTES.showMessage}
            element={<MessagePage />}
          />
          <Route
            path={CONSTANTS.ROUTES.auth2fa}
            element={<TwoFactorAuthPage />}
          />

          <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
            <Route
              path={CONSTANTS.ROUTES.adminPannel}
              element={<AdminPannel />}
            />
          </Route>

          <Route
            element={
              <ProtectedRoute allowedRoles={["admin", "psychiatrist"]} />
            }>
            <Route
              path={CONSTANTS.ROUTES.addMedication}
              element={
                <Suspense fallback={<FullScreenLoader />}>
                  <AddMedicationPage />
                </Suspense>
              }
            />
          </Route>

          <Route
            element={
              <ProtectedRoute
                allowedRoles={["admin", "guide", "psychiatrist"]}
              />
            }>
            <Route path={CONSTANTS.ROUTES.home} element={<Home />} />
            <Route
              path={CONSTANTS.ROUTES.addPatients}
              element={<AddPatientPage />}
            />
            <Route path={CONSTANTS.ROUTES.archive} element={<ArchivePage />} />
            <Route path={CONSTANTS.ROUTES.guides} element={<GuidePage />} />
            <Route path={CONSTANTS.ROUTES.doctors} element={<DoctorsPage />} />
            <Route
              path={CONSTANTS.ROUTES.patientDetails}
              element={<PatientDetailsPage />}
            />
            <Route
              path={CONSTANTS.ROUTES.enterExitPatients}
              element={<EnterExitPatientsPage />}
            />
            <Route
              path={CONSTANTS.ROUTES.pillsManager}
              element={<PillsManagerPage />}
            />
          </Route>

          <Route
            path={CONSTANTS.ROUTES.notAllowed}
            element={<UnauthorizedPage />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
